import { createFileRoute } from "@tanstack/react-router";
import {
  Button,
  Chip,
  FieldError,
  Input,
  Label,
  Modal,
  Skeleton,
  TextField,
} from "@heroui/react";
import { useEffect, useRef, useState } from "react";

import {
  OrderStatus,
  useAccountedPackage,
  useAccountOrder,
  useOrderByBarcode,
  useOrderInvoiceDocumentByPackageId,
} from "@/features/order/use-orders.ts";
import { Package } from "@/features/order/types.ts";
import { orderTools } from "@/shared/helper/order.ts";
import { PageLayout } from "@/shared/components/page-layout.ui.tsx";

export const Route = createFileRoute("/dashboard/courier_packages_accept")({
  component: RouteComponent,
});

const statusColorMap: Record<
  OrderStatus,
  "success" | "danger" | "warning" | "accent" | "default"
> = {
  created: "default",
  cancelled: "danger",
  accepted: "accent",
  going_to_sender: "accent",
  decided: "danger",
  submitted: "warning",
  accounted: "warning",
  checked: "default",
  revision: "danger",
  dispatched: "default",
  transit: "accent",
  arrived: "accent",
  assigned: "accent",
  delivers_recipient: "accent",
  completed: "success",
};

const statusBorderMap: Record<OrderStatus, string> = {
  created: "border-t-neutral-400",
  cancelled: "border-t-red-400",
  accepted: "border-t-blue-400",
  going_to_sender: "border-t-blue-400",
  decided: "border-t-red-400",
  submitted: "border-t-amber-400",
  accounted: "border-t-amber-400",
  checked: "border-t-purple-400",
  revision: "border-t-red-400",
  dispatched: "border-t-purple-400",
  transit: "border-t-blue-400",
  arrived: "border-t-blue-400",
  assigned: "border-t-blue-400",
  delivers_recipient: "border-t-blue-400",
  completed: "border-t-green-500",
};

function OrderSkeleton() {
  return (
    <div className="max-w-[960px] mx-auto w-full flex flex-col gap-4">
      <Skeleton className="h-24 w-full rounded-2xl" />
      <div className="grid grid-cols-3 gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-40 rounded-2xl" />
        ))}
      </div>
    </div>
  );
}

function PackageCard({
  pkg,
  isScanned,
  onAccept,
  isAccepting,
  onPrint,
  isPrinting,
}: {
  pkg: Package;
  isScanned: boolean;
  onAccept: () => void;
  isAccepting: boolean;
  onPrint: () => void;
  isPrinting: boolean;
}) {
  const isAccounted = pkg.status === "accounted";
  const isSubmitted = pkg.status === "submitted";

  let cardClass =
    "rounded-2xl border-2 p-4 flex flex-col gap-3 transition-all duration-200";

  if (isScanned && isAccounted) {
    cardClass += " border-green-400 bg-green-50 shadow-md";
  } else if (isScanned) {
    cardClass += " border-blue-400 bg-blue-50 shadow-md ring-2 ring-blue-200";
  } else {
    cardClass += " border-neutral-200 bg-white";
  }

  return (
    <div className={cardClass}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-neutral-800 text-sm leading-tight">
          {pkg.name}
        </span>
        <Chip
          color={statusColorMap[pkg.status] ?? "default"}
          size="sm"
          variant="soft"
        >
          {orderTools.status.toString(pkg.status)}
        </Chip>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-neutral-600">
        <div>
          <span className="text-neutral-400">Вес: </span>
          {pkg.weight} кг
        </div>
        {pkg.volumetricWeight ? (
          <div>
            <span className="text-neutral-400">Объём. вес: </span>
            {pkg.volumetricWeight} кг
          </div>
        ) : null}
        <div className="col-span-2">
          <span className="text-neutral-400">Размер: </span>
          {pkg.length}×{pkg.width}×{pkg.height} см
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {pkg.isFragile && (
          <Chip color="warning" size="sm" variant="soft">
            Хрупкий
          </Chip>
        )}
        {pkg.isDefect && (
          <Chip color="danger" size="sm" variant="soft">
            Дефект
          </Chip>
        )}
        {pkg.sellingPrice ? (
          <Chip color="default" size="sm" variant="soft">
            {pkg.sellingPrice.toLocaleString("ru-RU")} ₸
          </Chip>
        ) : null}
      </div>

      {pkg.photos && pkg.photos.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-neutral-400">Фото посылки</span>
          <div className="flex flex-wrap gap-1.5">
            {pkg.photos.map((url, i) => (
              <a
                key={i}
                href={url}
                rel="noreferrer"
                target="_blank"
              >
                <img
                  alt={`Фото ${i + 1}`}
                  className="size-14 rounded-lg object-cover border border-neutral-200"
                  src={url}
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {pkg.defectPhotos && pkg.defectPhotos.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-red-400">Фото дефектов</span>
          <div className="flex flex-wrap gap-1.5">
            {pkg.defectPhotos.map((url, i) => (
              <a
                key={i}
                href={url}
                rel="noreferrer"
                target="_blank"
              >
                <img
                  alt={`Дефект ${i + 1}`}
                  className="size-14 rounded-lg object-cover border border-red-200"
                  src={url}
                />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-1">
        <Button
          className="flex-1"
          isPending={isPrinting}
          size="sm"
          variant="outline"
          onPress={onPrint}
        >
          Распечатать
        </Button>

        {isScanned && isSubmitted && (
          <Button
            className="flex-1"
            isPending={isAccepting}
            size="sm"
            variant="primary"
            onPress={onAccept}
          >
            Принять посылку
          </Button>
        )}
      </div>

      {isScanned && isAccounted && (
        <div className="flex items-center gap-2 text-sm text-green-700 font-medium mt-1">
          <svg
            className="size-4 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              d="M20 6L9 17l-5-5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Принята на склад
        </div>
      )}
    </div>
  );
}

function AcceptOrderButton({
  disabled,
  isPending,
  onConfirm,
}: {
  disabled: boolean;
  isPending: boolean;
  onConfirm: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        className="w-full max-w-md"
        isDisabled={disabled}
        isPending={isPending}
        size="lg"
        variant="primary"
        onPress={() => setIsOpen(true)}
      >
        Принять заказ
      </Button>

      <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
        <Modal.Backdrop isDismissable>
          <Modal.Container placement="center">
            <Modal.Dialog>
              <Modal.Header>
                <Modal.Heading>Принять заказ?</Modal.Heading>
              </Modal.Header>
              <Modal.Body>
                Заказ будет принят на склад. Продолжить?
              </Modal.Body>
              <Modal.Footer>
                <Button variant="ghost" onPress={() => setIsOpen(false)}>
                  Отмена
                </Button>
                <Button
                    isPending={isPending}
                    variant="primary"
                    onPress={() => {
                      setIsOpen(false);
                      onConfirm();
                    }}
                >
                  Принять
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </>
  );
}

function RouteComponent() {
  const [inputBarcode, setInputBarcode] = useState("");
  const [activeBarcode, setActiveBarcode] = useState("");

  const activeBarcodeRef = useRef(activeBarcode);
  const scannedPackageRef = useRef<Package | undefined>(undefined);
  const orderLoadedRef = useRef(false);

  const { data, refetch, isFetching } = useOrderByBarcode({
    enabled: activeBarcode.length > 5,
    barcode: activeBarcode,
  });

  const { mutateAsync: acceptPackage, isPending: isAccepting } =
    useAccountedPackage({
      onSuccess: () => {
        refetch();
      },
    });

  const { mutateAsync: acceptOrder, isPending: isAcceptingOrder } =
    useAccountOrder({
      onSuccess: () => {
        refetch();
      },
    });

  const doc = useOrderInvoiceDocumentByPackageId();
  const [printingPackageId, setPrintingPackageId] = useState<number | null>(
    null,
  );

  const order = data?.data;

  const scannedPackage = order?.packages.find((pkg) =>
    pkg.identifications?.some((id) => id.uuid === activeBarcode),
  );

  useEffect(() => {
    activeBarcodeRef.current = activeBarcode;
  }, [activeBarcode]);

  useEffect(() => {
    scannedPackageRef.current = scannedPackage;
  }, [scannedPackage]);

  useEffect(() => {
    orderLoadedRef.current = !!order;
  }, [order]);

  useEffect(() => {
    if (inputBarcode.length <= 5) return;

    const timer = setTimeout(() => {
      const current = activeBarcodeRef.current;

      if (inputBarcode === current && orderLoadedRef.current) {
        const pkg = scannedPackageRef.current;

        if (pkg && pkg.status === "submitted") {
          acceptPackage({ barcode: inputBarcode });
        }
        setInputBarcode("");
      } else if (inputBarcode !== current) {
        setActiveBarcode(inputBarcode);
        setInputBarcode("");
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputBarcode]);

  const isSearching = activeBarcode.length > 5 && isFetching;

  const acceptedCount = order?.packages.filter(
    (p) => p.status === "accounted",
  ).length;
  const totalCount = order?.packages.length ?? 0;

  return (
    <PageLayout title="Приёмка посылок у курьера">
      <div className="flex flex-col pt-4">
        <div className="flex justify-center py-8">
          <div className="flex flex-col items-center gap-4 bg-neutral-50 border border-neutral-200 rounded-2xl px-8 py-6 w-full max-w-md shadow-sm">
            <svg
              className="text-neutral-300"
              fill="none"
              height="48"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
              width="48"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect height="6" rx="1" width="6" x="3" y="3" />
              <rect height="6" rx="1" width="6" x="15" y="3" />
              <rect height="6" rx="1" width="6" x="3" y="15" />
              <path d="M15 15h2v2h-2zM19 15h2v2h-2zM19 19h2v2h-2zM15 19h2v2h-2z" />
            </svg>

            <TextField className="w-full">
              <Label>QR-код посылки</Label>
              <Input
                autoFocus
                className="w-full"
                type="text"
                value={inputBarcode}
                onChange={(e) => setInputBarcode(e.target.value)}
              />
              <FieldError />
              <p className="text-xs text-neutral-500 mt-1">
                Отсканируйте QR-код посылки или введите вручную
              </p>
            </TextField>

            {activeBarcode && order && (
              <div className="w-full text-center text-sm text-neutral-500">
                Заказ{" "}
                <span className="font-semibold text-neutral-700">
                  {order.orderTitle}
                </span>
                {" · "}
                <span className="text-green-600 font-medium">
                  {acceptedCount}
                </span>
                /{totalCount} принято
              </div>
            )}

            {!activeBarcode && (
              <p className="text-sm text-neutral-400 text-center">
                Отсканируйте QR-код посылки для загрузки заказа.
                <br />
                Повторное сканирование принятой QR — автоматически примет
                посылку.
              </p>
            )}
          </div>
        </div>

        {isSearching && <OrderSkeleton />}

        {order && !isSearching && (
          <div className="max-w-[960px] mx-auto w-full flex flex-col gap-6 pb-24">
            <div
              className={`border-t-4 ${statusBorderMap[order.status]} rounded-2xl border border-neutral-200 bg-white shadow-sm p-5`}
            >
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <span className="text-xl font-bold text-neutral-800">
                  {order.orderTitle}
                </span>
                <Chip
                  color={statusColorMap[order.status] ?? "default"}
                  size="md"
                  variant="primary"
                >
                  {orderTools.status.toString(order.status)}
                </Chip>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-neutral-600">
                <div>
                  <span className="font-medium text-neutral-500">
                    Отправитель:{" "}
                  </span>
                  {order.fromName} · {order.fromPhone}
                </div>
                <div>
                  <span className="font-medium text-neutral-500">
                    Получатель:{" "}
                  </span>
                  {order.toName} · {order.toPhone}
                </div>
                <div>
                  <span className="font-medium text-neutral-500">Откуда: </span>
                  {order.fromCityName}, {order.fromAddress}
                </div>
                <div>
                  <span className="font-medium text-neutral-500">Куда: </span>
                  {order.toCityName}, {order.toAddress}
                </div>
              </div>
            </div>

            {order.packages.length > 0 && (
              <div>
                <div className="flex items-center justify-between border-b border-neutral-200 pb-2 mb-4">
                  <span className="font-semibold text-base text-neutral-700">
                    Посылки
                  </span>
                  <span className="text-sm text-neutral-400">
                    Принято:{" "}
                    <span className="font-medium text-green-600">
                      {acceptedCount}
                    </span>
                    /{totalCount}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {order.packages.map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      isAccepting={isAccepting}
                      isPrinting={printingPackageId === pkg.id && doc.isPending}
                      isScanned={
                        pkg.identifications?.some(
                          (id) => id.uuid === activeBarcode,
                        ) ?? false
                      }
                      pkg={pkg}
                      onAccept={() => {
                        const identification = pkg.identifications?.find(
                          (i) => i.target === "ORDER_QR_TARGET_COURIER",
                        );

                        if (identification) {
                          acceptPackage({ barcode: identification.uuid });
                        }
                      }}
                      onPrint={() => {
                        setPrintingPackageId(pkg.id);
                        doc.mutateAsync({ packageId: pkg.id });
                      }}
                    />
                  ))}
                </div>

                {order.status === "accounted" ? (
                  <div className="mt-6 flex items-center justify-center gap-2 text-green-700 font-medium">
                    <svg
                      className="size-5 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M20 6L9 17l-5-5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Заказ принят на склад
                  </div>
                ) : (
                  <div className="mt-6 flex flex-col items-center gap-2">
                    <AcceptOrderButton
                      disabled={(acceptedCount ?? 0) !== totalCount}
                      isPending={isAcceptingOrder}
                      onConfirm={() => acceptOrder({ orderId: order.orderId })}
                    />
                    {(acceptedCount ?? 0) !== totalCount && (
                      <p className="text-xs text-neutral-400 text-center">
                        Примите все посылки, чтобы принять заказ
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
